import { useState, useEffect, useCallback, useRef } from 'react';
import { HiPlay, HiBell, HiBellSlash } from 'react-icons/hi2';
import { getStationSchedule } from '../api/client';
import type { Stream, ScheduleDay, ScheduleItem } from '../types';

interface SchedulePanelProps {
  stream: Stream;
  onNavigate?: (episodeId: number) => void;
  onGoLive?: () => void;
  fallbackImage?: string;
  isPWAMode?: boolean;
  isFollowing?: (showId: number) => boolean;
  onFollow?: (showId: number, showTitle: string, image?: string) => void;
  onUnfollow?: (showId: number) => void;
  onCurrentShowChange?: (showId: number | null) => void;
}

function ShowCard({
  show,
  fallbackImage,
  onNavigate,
  onGoLive,
  isPWAMode,
  isFollowing,
  onFollow,
  onUnfollow,
  isLarge,
}: {
  show: ScheduleItem;
  fallbackImage?: string;
  onNavigate?: (episodeId: number) => void;
  onGoLive?: () => void;
  isPWAMode?: boolean;
  isFollowing?: (showId: number) => boolean;
  onFollow?: (showId: number, showTitle: string, image?: string) => void;
  onUnfollow?: (showId: number) => void;
  isLarge?: boolean;
}) {
  const following = isFollowing && show.showId ? isFollowing(show.showId) : false;
  const isClickable = show.isCurrent && onGoLive;

  return (
    <div
      className={`flex gap-3 rounded-xl transition-all group
                  ${isLarge ? 'p-3' : 'p-2.5'}
                  ${show.isCurrent
                    ? 'bg-[var(--color-primary)]/15 border border-[var(--color-primary)]/30'
                    : show.isPast
                      ? 'opacity-40'
                      : 'bg-[var(--color-surface)]/30 hover:bg-[var(--color-surface)]/50 border border-transparent'
                  }
                  ${isClickable ? 'cursor-pointer hover:bg-[var(--color-primary)]/25' : ''}`}
      onClick={isClickable ? onGoLive : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onGoLive!(); } } : undefined}
    >
      {/* Time column */}
      <div className={`flex-shrink-0 ${isLarge ? 'w-16' : 'w-14'} pt-0.5`}>
        <p className={`font-semibold tabular-nums leading-tight
                      ${isLarge ? 'text-sm' : 'text-xs'}
                      ${show.isCurrent ? 'text-[var(--color-primary)]' : 'text-[var(--color-text)]'}`}>
          {show.startTime}
        </p>
        <p className={`text-[var(--color-text-muted)] tabular-nums
                      ${isLarge ? 'text-xs' : 'text-[10px]'}`}>
          {show.endTime}
        </p>
      </div>

      {/* Show image */}
      <div className={`relative flex-shrink-0 rounded-lg overflow-hidden bg-[var(--color-surface)]/40
                       ${isLarge ? 'w-14 h-14' : 'w-11 h-11'}`}>
        <img
          src={show.image || fallbackImage}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (fallbackImage && target.src !== fallbackImage) {
              target.src = fallbackImage;
            }
          }}
        />
        {/* Play overlay for listen again */}
        {show.episodeId && onNavigate && (
          <button
            onClick={() => onNavigate(show.episodeId!)}
            className="absolute inset-0 bg-black/60 flex items-center justify-center
                       opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            title="Listen again"
          >
            <HiPlay size={18} className="text-white" />
          </button>
        )}
        {/* Live pulse */}
        {show.isCurrent && (
          <div className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse
                         ring-2 ring-red-500/30" />
        )}
      </div>

      {/* Show info */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <p className={`font-medium leading-snug
                      ${isLarge ? 'text-sm' : 'text-xs'}
                      ${show.isCurrent ? 'text-[var(--color-primary)]' : 'text-[var(--color-text)]'}`}>
          {show.title}
        </p>
        {show.isCurrent && (
          <p className="text-[10px] font-semibold text-red-400 uppercase tracking-wide mt-0.5">
            On Air{onGoLive ? ' · Tap to listen' : ''}
          </p>
        )}
        {show.episodeId && !show.isCurrent && (
          <p className="text-[10px] text-[var(--color-text-muted)] mt-0.5">
            Listen again available
          </p>
        )}
      </div>

      {/* Notification bell (PWA) */}
      {isPWAMode && show.showId && isFollowing && onFollow && onUnfollow && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (following) {
              onUnfollow(show.showId);
            } else {
              onFollow(show.showId, show.title, show.image);
            }
          }}
          className={`flex-shrink-0 self-center p-2 rounded-full transition-colors
                     ${following
                       ? 'text-[var(--color-primary)] bg-[var(--color-primary)]/10'
                       : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] opacity-0 group-hover:opacity-100'
                     }`}
          title={following ? 'Stop notifications' : 'Get notified when live'}
        >
          {following ? <HiBell size={16} /> : <HiBellSlash size={16} />}
        </button>
      )}
    </div>
  );
}

function DaySection({
  day,
  fallbackImage,
  onNavigate,
  onGoLive,
  isPWAMode,
  isFollowing,
  onFollow,
  onUnfollow,
  currentShowRef,
  isLarge,
}: {
  day: ScheduleDay;
  fallbackImage?: string;
  onNavigate?: (episodeId: number) => void;
  onGoLive?: () => void;
  isPWAMode?: boolean;
  isFollowing?: (showId: number) => boolean;
  onFollow?: (showId: number, showTitle: string, image?: string) => void;
  onUnfollow?: (showId: number) => void;
  currentShowRef: React.RefObject<HTMLDivElement | null>;
  isLarge: boolean;
}) {
  // For today, hide past shows (keep current and future)
  const visibleShows = day.isToday
    ? day.shows.filter(show => show.isCurrent || !show.isPast)
    : day.shows;

  if (visibleShows.length === 0) return null;

  return (
    <div>
      {/* Day header */}
      <div className={`sticky top-0 z-10 px-4 py-2 backdrop-blur-md border-b border-[var(--color-border)]/30
                       ${day.isToday
                         ? 'bg-[var(--color-primary)]/10'
                         : 'bg-[var(--color-surface)]/60'
                       }`}>
        <div className="max-w-2xl mx-auto flex items-baseline gap-2">
          <span className={`text-sm font-bold
                           ${day.isToday ? 'text-[var(--color-primary)]' : 'text-[var(--color-text)]'}`}>
            {day.isToday ? 'Today' : day.dayName}
          </span>
          <span className="text-xs text-[var(--color-text-muted)]">{day.date}</span>
        </div>
      </div>

      {/* Shows list */}
      <div className="max-w-2xl mx-auto px-4 py-2 space-y-1">
        {visibleShows.map((show, index) => (
          <div
            key={`${show.showId}-${show.startTime}-${index}`}
            ref={show.isCurrent ? currentShowRef : undefined}
          >
            <ShowCard
              show={show}
              fallbackImage={fallbackImage}
              onNavigate={onNavigate}
              onGoLive={day.isToday ? onGoLive : undefined}
              isPWAMode={isPWAMode}
              isFollowing={isFollowing}
              onFollow={onFollow}
              onUnfollow={onUnfollow}
              isLarge={isLarge}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function SchedulePanel({
  stream,
  onNavigate,
  onGoLive,
  fallbackImage,
  isPWAMode,
  isFollowing,
  onFollow,
  onUnfollow,
  onCurrentShowChange,
}: SchedulePanelProps) {
  const [days, setDays] = useState<ScheduleDay[]>([]);
  const [loading, setLoading] = useState(true);
  const currentShowRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  const fetchSchedule = useCallback(async () => {
    try {
      const data = await getStationSchedule(stream.streamid);
      setDays(data.days);
    } catch (err) {
      console.error('Failed to fetch schedule:', err);
    } finally {
      setLoading(false);
    }
  }, [stream.streamid]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  // Poll for updates
  useEffect(() => {
    const interval = setInterval(fetchSchedule, 60000);
    return () => clearInterval(interval);
  }, [fetchSchedule]);

  // Notify parent of currently live show
  useEffect(() => {
    if (!onCurrentShowChange) return;
    for (const day of days) {
      const currentShow = day.shows.find(show => show.isCurrent);
      if (currentShow && currentShow.showId) {
        onCurrentShowChange(currentShow.showId);
        return;
      }
    }
    onCurrentShowChange(null);
  }, [days, onCurrentShowChange]);

  // Auto-scroll to current show
  useEffect(() => {
    if (hasScrolledRef.current || days.length === 0) return;
    const timer = setTimeout(() => {
      if (currentShowRef.current && scrollContainerRef.current) {
        currentShowRef.current.scrollIntoView({ block: 'start' });
        hasScrolledRef.current = true;
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [days]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-[var(--color-border)]
                          border-t-[var(--color-primary)] rounded-full animate-spin" />
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">Loading schedule...</p>
        </div>
      </div>
    );
  }

  if (days.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-[var(--color-text-muted)]">No schedule available</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto" ref={scrollContainerRef}>
        {days.map((day) => (
          <DaySection
            key={day.date}
            day={day}
            fallbackImage={fallbackImage || stream.image}
            onNavigate={onNavigate}
            onGoLive={onGoLive}
            isPWAMode={isPWAMode}
            isFollowing={isFollowing}
            onFollow={onFollow}
            onUnfollow={onUnfollow}
            currentShowRef={currentShowRef}
            isLarge={true}
          />
        ))}
      </div>
    </div>
  );
}
